import { AxiosResponse } from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  Configuration,
  AuthApi,
  UserAuthResponse,
  UserRegistrationResponse,
  PasswordResetTokenProperties,
  AddProfileDto,
  AcceptTermsandConditionsDto,
  SwitchProfileDto,
  LoginAsDto,
  UserDetailsDto,
  UpdateUserDetailsDto,
  RegistrationTokenRequestResponseDto,
  TempPasscodeResponseDto,
  TempPasscodeRequestDto,
  UserRegistrationV2Dto,
  HealthcheckApi,
  AccountActivationSubjectDto,
  SelfInviteProfileDto,
  RegistrationTokenRequestDto,
} from '@inosio/payments-sdk';
import {
  getPasswordLogin,
  loginFailed,
  setCredentials,
  setAccountActivationDetails,
  getAccountActivationDetails,
  getRegisterUser,
  setRegisterUser,
  setRegisterUserError,
  setValidateTokenError,
  getPasswordRestTokenDetails,
  setPasswordRestTokenDetails,
  issueTempPasscode,
  issueTempPasscodeFailed,
  sendInvitation,
  sendInvitationFailed,
  setIssueTempPasscodeResult,
  setSendInvitationResult,
  setRegisterUserV2,
  getRegisterUserV2,
  getHealthCheck,
  setHealthCheck,
  setHealthCheckError,
  setErrorSelfInviteProfile,
  setSelfInviteProfile,
  selfInviteProfile,
  setPasswordResetRequestError,
  setPasswordResetRequest,
  getMfaLogin,
  setMfaRequired,
} from './authSlice';
import {
  getResetPasswordWithToken,
  setResetPasswordWithToken,
  getPasswordResetRequest,
  getAddProfile,
  IpaUserExtended,
  selectCurrentUser,
  getRecordAgreement,
  setRecordAgreement,
  setGlobalError,
  setAddProfile,
  switchProfileRequestReceived,
  clearCredentials,
  getUserDetails,
} from './authSlice';
import { setUserDetails, updateUserDetails, updateUserDetailsFailed, setResUiError, getLoginAsEntity, setLoginAsEntity, setResetPasswordWithTokenError } from './authSlice';
import { IPA_MFA_TOKEN_NAME } from '../../../utils/constants';

const { REACT_APP_IPA_BASE_PATH } = process.env;
const apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH });

export function requestSelfRegisterOwner(selfInviteProfileDto: SelfInviteProfileDto) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.selfInviteProfile(selfInviteProfileDto);
}

export function remoteApiPasswordLogin(email: string, password: string) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.userLoginPassword({ email: email, password: password });
}

export function remoteApiMfaLogin(passcode: string, mfaAccessToken: string) {
  const _apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH, accessToken: mfaAccessToken });

  const ipaAuthAPI = new AuthApi(_apiConfig);
  return ipaAuthAPI.userLoginMfa({passcode: passcode });
}

export function getHealthcheck() {
  const ipaAuthAPI = new HealthcheckApi(apiConfig);
  return ipaAuthAPI.getHealthCheck();
}

export function switchPrincipalApiCall(access_token: string, principalUuid: string) {
  const _apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH, accessToken: access_token });
  const ipaAuthAPI = new AuthApi(_apiConfig);
  const switchProfileDto: SwitchProfileDto = {
    PrincipalUuid: principalUuid,
  };
  return ipaAuthAPI.setPrincipal(switchProfileDto);
}

export function getRegisterUserDetailsAPIcall(registrationDetails: UserRegistrationV2Dto) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.userRegistrationV2(registrationDetails);
}

export function registerUserV2Apicall(userRegistrationV2Dto: UserRegistrationV2Dto) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.userRegistrationV2(userRegistrationV2Dto);
}

export function getRequestAddProfile(access_token: string, activationTokendto: AddProfileDto) {
  const newApiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAuthAPI = new AuthApi(newApiConfig);

  return ipaAuthAPI.addProfile(activationTokendto);
}

export function getRequestUserDetails(access_token: string) {
  const newApiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAuthAPI = new AuthApi(newApiConfig);

  return ipaAuthAPI.getUserDetails();
}

export function updateRequestUserDetails(access_token: string, updateDetails: UpdateUserDetailsDto) {
  const newApiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAuthAPI = new AuthApi(newApiConfig);

  return ipaAuthAPI.updateUserDetails(updateDetails);
}

export function getRequestRecordAgreement(access_token: string, acceptTermsDto: AcceptTermsandConditionsDto) {
  const newApiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAuthAPI = new AuthApi(newApiConfig);
  return ipaAuthAPI.acceptterms(acceptTermsDto);
}

export function getAccountVerifctionDetailsAPIcall(activationToken: string) {
  const ipaAuthAPI = new AuthApi(apiConfig);

  // decode token to determine entity type
  // const entityType = jwtDecode<tokenObject>(activationToken).entityType === "Property Manager User" ? "pm" : "res";

  return ipaAuthAPI.validateactivationtoken(activationToken);
}

export function submitPasswordResetrequest(email: string) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.requestPasswordReset({ email: email });
}

export function validatePasswordResetToken(token: string) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.validatePasswordResetToken(token);
}

export function submitPasswordResetChange(token: string, password: string, email?: string, oldPassword?: string) {
  const ipaAuthAPI = new AuthApi(apiConfig);
  return ipaAuthAPI.resetPasswordWithToken(token, { password: password, email: email, oldPassword: oldPassword });
}

export function requestLoginAsResident(access_token: string, loginAsDto: LoginAsDto) {
  const newApiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken: access_token,
  });
  const ipaAuthAPI = new AuthApi(newApiConfig);

  return ipaAuthAPI.loginAs(loginAsDto);
}

export function requestSendInvitation(access_token: string, registrationTokenRequestDto: RegistrationTokenRequestDto, sendEmail: boolean) {
  apiConfig.accessToken = access_token;
  const ipaAuthAPI = new AuthApi(apiConfig);

  return ipaAuthAPI.createActivationToken(registrationTokenRequestDto, sendEmail);
}

export function requestIssueTempPasscode(access_token: string, tempPasscodeResponseDto: TempPasscodeRequestDto) {
  apiConfig.accessToken = access_token;
  const ipaAuthAPI = new AuthApi(apiConfig);

  return ipaAuthAPI.issueTempPasscode(tempPasscodeResponseDto);
}

function* handlePasswordLogin(action: { type: typeof getPasswordLogin; payload: { email: string; password: string } }) {
  try {
    const loginResponse: AxiosResponse<UserAuthResponse> = yield call(remoteApiPasswordLogin, action.payload.email, action.payload.password);
    const { data } = loginResponse;
    if (data.mfaRequired) {
      yield put(setMfaRequired({ ...data }));
    } else {
      yield put(setCredentials({ ...data }));
    }
  } catch (error: any) {
    const { isLockedForSecurity, wrongPasswordAttempts, maxWrongPasswordAttempts } = error?.response?.data;

    yield put(
      loginFailed({
        message: error.response.data.message,
        isLockedForSecurity,
        wrongPasswordAttempts,
        maxWrongPasswordAttempts,
      }),
    );
  }
}

function* handleMfaLogin(action: { type: typeof getPasswordLogin; payload: { passcode: string } }) {
  try {
    const mfaAccessToken = sessionStorage.getItem(IPA_MFA_TOKEN_NAME);
    const loginResponse: AxiosResponse<UserAuthResponse> = yield call(remoteApiMfaLogin, action.payload.passcode, mfaAccessToken!);
    const { data } = loginResponse;
    yield put(setCredentials({ ...data }));
  } catch (error: any) {
    const { isLockedForSecurity, wrongPasswordAttempts, maxWrongPasswordAttempts } = error?.response?.data;

    yield put(
      loginFailed({
        message: error.response.data.message,
        isLockedForSecurity,
        wrongPasswordAttempts,
        maxWrongPasswordAttempts,
      }),
    );
  }
}

function* handleGetAccountActivationDetails(action: { type: typeof getAccountActivationDetails; payload: { activationToken: string } }) {
  try {
    const accountDetails: AxiosResponse<AccountActivationSubjectDto> = yield call(getAccountVerifctionDetailsAPIcall, action.payload.activationToken);
    const { data } = accountDetails;
    yield put(setAccountActivationDetails({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setValidateTokenError({ ...error?.response?.data }));
    } else {
      yield put(setValidateTokenError({ ...error }));
    }
  }
}

function* handleGetRegisterUserDetails(action: { type: typeof getRegisterUser; payload: UserRegistrationV2Dto }) {
  try {
    const registrationDetails = {
      Email: action.payload.Email,
      Password: action.payload.Password,
      FirstName: action.payload.FirstName,
      MiddleName: action.payload.MiddleName,
      LastName: action.payload.LastName,
    } as UserRegistrationV2Dto;

    const registartionResponse: AxiosResponse<UserRegistrationResponse> = yield call(getRegisterUserDetailsAPIcall, registrationDetails);
    const { data } = registartionResponse;
    yield put(setRegisterUser({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setRegisterUserError({ ...error?.response?.data }));
    } else {
      yield put(setRegisterUserError({ ...error }));
    }
  }
}

function* handleRegisterUserDetailsV2(action: { type: typeof getRegisterUserV2; payload: UserRegistrationV2Dto }) {
  try {
    const registationV2Response: AxiosResponse<UserRegistrationResponse> = yield call(registerUserV2Apicall, action.payload);
    const { data } = registationV2Response;
    yield put(setRegisterUserV2({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setRegisterUserError({ ...error?.response?.data }));
    } else {
      yield put(setRegisterUserError({ ...error }));
    }
  }
}

function* handleSendPasswordResetTokenRequest(action: { type: typeof getPasswordResetRequest; payload: { email: string } }) {
  try {
    const resetTokenDetails: AxiosResponse<void> = yield call(submitPasswordResetrequest, action.payload.email);
    if (resetTokenDetails.status) yield put(setPasswordResetRequest({}));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setPasswordResetRequestError({ ...error?.response?.data })); //todo: make specific error
    } else {
      yield put(setPasswordResetRequestError({ ...error }));
    }
  }
}

function* handleGetPasswordResetTokenDetails(action: { type: typeof getPasswordRestTokenDetails; payload: { passwordResetToken: string } }) {
  try {
    const resetTokenDetails: AxiosResponse<PasswordResetTokenProperties> = yield call(validatePasswordResetToken, action.payload.passwordResetToken);
    const { data } = resetTokenDetails;
    yield put(setPasswordRestTokenDetails({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setValidateTokenError({ ...error?.response?.data }));
    } else {
      yield put(setValidateTokenError({ ...error }));
    }
  }
}

function* handleResetPasswordWithToken(action: { type: typeof getResetPasswordWithToken; payload: { token: string; password: string; username?: string; oldPassword?: string } }) {
  try {
    const resetResponse: AxiosResponse<void> = yield call(submitPasswordResetChange, action.payload.token, action.payload.password, action.payload.username, action.payload.oldPassword);
    if (resetResponse.status) yield put(setResetPasswordWithToken({})); //just a 200 ok
  } catch (error: any) {
    if (error?.response?.data?.statusCode === 401) {
      yield put(setResetPasswordWithTokenError({ ...error?.response?.data }));
    } else {
      yield put(setResetPasswordWithTokenError({ ...error?.response?.data, message: 'Something went wrong.' }));
    }
  }
}

function* handleGetAddPorfile(action: { type: typeof getAddProfile; payload: AddProfileDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const registartionResponse: AxiosResponse<UserAuthResponse> = yield call(getRequestAddProfile, currentUser.accessToken, action.payload);
    const { data } = registartionResponse;
    yield put(setAddProfile({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setGlobalError({ ...error?.response?.data }));
    } else {
      yield put(setGlobalError({ ...error }));
    }
  }
}

function* handleGetRecordAgreement(action: { type: typeof getRecordAgreement; payload: AcceptTermsandConditionsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    yield call(getRequestRecordAgreement, currentUser.accessToken, action.payload);
    yield put(setRecordAgreement());
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setGlobalError({ ...error?.response?.data }));
    } else {
      yield put(setGlobalError({ ...error }));
    }
  }
}

function* handleSwitchProfile(action: { type: typeof switchProfileRequestReceived; payload: { principalUuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const newProfileToken: AxiosResponse<UserAuthResponse> = yield call(switchPrincipalApiCall, currentUser.accessToken, action.payload.principalUuid);
    const { data } = newProfileToken;
    yield put(clearCredentials());
    yield put(setCredentials({ ...data, profileChange: true }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setValidateTokenError({ ...error?.response?.data }));
    } else {
      yield put(setValidateTokenError({ ...error }));
    }
  }
}

function* handleGetUserDetails(action: { type: typeof getUserDetails }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const userDetailsResponse: AxiosResponse<UserDetailsDto> = yield call(getRequestUserDetails, currentUser.accessToken);
    const { data } = userDetailsResponse;
    yield put(setUserDetails({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setResUiError({ ...error?.response?.data }));
    } else {
      yield put(setResUiError({ ...error }));
    }
  }
}

function* handleUpdateUserDetails(action: { type: typeof updateUserDetails; payload: UpdateUserDetailsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const userDetailsResponse: AxiosResponse<UserDetailsDto> = yield call(updateRequestUserDetails, currentUser.accessToken, action.payload);
    const { data } = userDetailsResponse;
    yield put(setUserDetails({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(updateUserDetailsFailed({ ...error?.response?.data }));
    } else {
      yield put(updateUserDetailsFailed({ ...error }));
    }
  }
}

function* handleLoginAsResident(action: { type: typeof getLoginAsEntity; payload: LoginAsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser); // we'll use when we update the sdk
    const response: AxiosResponse<UserAuthResponse> = yield call(requestLoginAsResident, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(clearCredentials());
    yield put(setLoginAsEntity({ ...data }));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setValidateTokenError({ ...error?.response?.data }));
    } else {
      yield put(setValidateTokenError({ ...error }));
    }
  }
}

export function* handleSendInvitation(action: { type: typeof sendInvitation; payload: { registrationTokenRequestDto: RegistrationTokenRequestDto; sendEmail: boolean } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<RegistrationTokenRequestResponseDto> = yield call(
      requestSendInvitation,
      currentUser.accessToken,
      action.payload.registrationTokenRequestDto,
      action.payload.sendEmail,
    );
    const { data } = response;
    yield put(setSendInvitationResult(data));
  } catch (error: any) {
    yield put(sendInvitationFailed(error?.response?.data?.message || 'An unknown error has occurred.'));
  }
}

export function* handleIssueTempPasscode(action: { type: typeof issueTempPasscode; payload: TempPasscodeRequestDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<TempPasscodeResponseDto> = yield call(requestIssueTempPasscode, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setIssueTempPasscodeResult(data));
  } catch (error: any) {
    yield put(issueTempPasscodeFailed(error?.response?.data?.message || 'An unknown error has occurred.'));
  }
}

function* handleGetHealthcheck(action: { type: typeof getHealthCheck }) {
  try {
    const healthCheckResponse: AxiosResponse<UserDetailsDto> = yield call(getHealthcheck);
    const { data } = healthCheckResponse;
    yield put(setHealthCheck({ ...data }));
  } catch (error: any) {
    yield setHealthCheckError({ ...error?.response?.data });
  }
}

function* handleSelfRegisterOwner(action: { type: typeof selfInviteProfile; payload: SelfInviteProfileDto }) {
  try {
    yield call(requestSelfRegisterOwner, action.payload);
    yield put(setSelfInviteProfile());
  } catch (error: any) {
    yield put(
      setErrorSelfInviteProfile({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

function* authSaga() {
  yield takeLatest(getHealthCheck.type, handleGetHealthcheck);
  yield takeLatest(getPasswordLogin.type, handlePasswordLogin);
  yield takeLatest(getMfaLogin.type, handleMfaLogin);
  yield takeLatest(getAccountActivationDetails.type, handleGetAccountActivationDetails);
  yield takeLatest(getRegisterUser.type, handleGetRegisterUserDetails);
  yield takeLatest(getRegisterUserV2.type, handleRegisterUserDetailsV2);
  yield takeLatest(getPasswordRestTokenDetails.type, handleGetPasswordResetTokenDetails);
  yield takeLatest(getResetPasswordWithToken.type, handleResetPasswordWithToken);
  yield takeLatest(getPasswordResetRequest.type, handleSendPasswordResetTokenRequest);
  yield takeLatest(getAddProfile.type, handleGetAddPorfile);
  yield takeLatest(getRecordAgreement.type, handleGetRecordAgreement);
  yield takeLatest(switchProfileRequestReceived.type, handleSwitchProfile);
  yield takeLatest(getUserDetails.type, handleGetUserDetails);
  yield takeLatest(updateUserDetails.type, handleUpdateUserDetails);
  yield takeLatest(getLoginAsEntity.type, handleLoginAsResident);
  yield takeLatest(issueTempPasscode.type, handleIssueTempPasscode);
  yield takeLatest(sendInvitation.type, handleSendInvitation);
  yield takeLatest(selfInviteProfile.type, handleSelfRegisterOwner);
}

export default authSaga;
